import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, Alert, CircularProgress, Typography, Chip } from '@mui/joy';
import type { DrawingMode, DrawingState, RoofSection, RoofFeature, AreaCalculation } from '../../types';
import { MapToolbar } from './MapToolbar';
import { useLineDrawing } from './useLineDrawing';
import { usePolygonDrawing } from './usePolygonDrawing';
import {
  LINE_TYPE_CONFIG,
  LINE_TYPES,
  LINE_STROKE_WEIGHT,
  LINE_STROKE_WEIGHT_HOVER,
  LINE_STROKE_OPACITY,
  type LineType,
} from './lineDrawingConstants';

interface DrawableGoogleMapProps {
  center?: { lat: number; lng: number };
  zoom?: number;
  selectedLocation?: { lat: number; lng: number };
  height?: string | number;
  roofSections?: RoofSection[];
  onSectionCreate?: (section: Omit<RoofSection, 'id' | 'created_at' | 'updated_at'>) => void;
  onSectionUpdate?: (sectionId: number, updates: Partial<RoofSection>) => void;
  onSectionDelete?: (sectionId: number) => void;
  readOnly?: boolean;
  // Line drawing props (optional for backward compatibility)
  roofFeatures?: RoofFeature[];
  onFeatureCreate?: (feature: Omit<RoofFeature, 'id' | 'created_at' | 'updated_at'>) => void;
  onFeatureUpdate?: (featureId: number, updates: Partial<RoofFeature>) => void;
  onFeatureDelete?: (featureId: number) => void;
  measurementId?: number;
  // Visibility control
  visibleLineTypes?: Set<string>;
  onVisibleLineTypesChange?: (types: Set<string>) => void;
  // Hover highlighting
  highlightedFeatureId?: number | null;
  // Auto-draw review: render unaccepted auto_ai geometry in a pending style
  reviewPending?: boolean;
  // Group nudge applied to all auto_ai overlays during review (degrees)
  autoOffset?: { lat: number; lng: number };
}

export const DrawableGoogleMap: React.FC<DrawableGoogleMapProps> = ({
  center = { lat: 39.8283, lng: -98.5795 },
  zoom = 18,
  selectedLocation,
  height = 600,
  roofSections = [],
  onSectionCreate,
  onSectionUpdate,
  onSectionDelete,
  readOnly = false,
  roofFeatures = [],
  onFeatureCreate,
  onFeatureUpdate,
  onFeatureDelete,
  measurementId,
  visibleLineTypes: externalVisibleLineTypes,
  onVisibleLineTypesChange,
  highlightedFeatureId,
  reviewPending = false,
  autoOffset,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const polygonsRef = useRef<Map<number, google.maps.Polygon>>(new Map());
  const polylinesRef = useRef<Map<number, google.maps.Polyline>>(new Map());

  const labelMarkersRef = useRef<Map<number, google.maps.Marker>>(new Map());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);
  const [drawingState, setDrawingState] = useState<DrawingState>({
    mode: 'view',
    isDrawing: false,
    currentPoints: [],
    activeLineType: null,
  });
  const [areaCalculation, setAreaCalculation] = useState<AreaCalculation | null>(null);
  const [editedSections, setEditedSections] = useState<Map<number, google.maps.LatLng[]>>(new Map());
  const [selectedFeatureId, setSelectedFeatureId] = useState<number | null>(null);

  // Refs so map event callbacks and unmount cleanup always read current
  // values without forcing overlay rebuilds (rebuilding mid-drag would
  // revert the user's vertex edits).
  const editedFeaturesRef = useRef<Map<number, google.maps.LatLng[]>>(new Map());
  const editedSectionsRef = useRef<Map<number, google.maps.LatLng[]>>(new Map());
  const pendingReviewIdsRef = useRef<Set<number>>(new Set());
  const onFeatureUpdateRef = useRef(onFeatureUpdate);
  const onSectionUpdateRef = useRef(onSectionUpdate);
  const highlightedFeatureIdRef = useRef(highlightedFeatureId);
  useEffect(() => { onFeatureUpdateRef.current = onFeatureUpdate; }, [onFeatureUpdate]);
  useEffect(() => { onSectionUpdateRef.current = onSectionUpdate; }, [onSectionUpdate]);
  useEffect(() => { highlightedFeatureIdRef.current = highlightedFeatureId; }, [highlightedFeatureId]);

  const [snapEnabled, setSnapEnabled] = useState(true);
  const [showDimensions, setShowDimensions] = useState(true);

  // Internal visible line types state (used if no external control)
  const [internalVisibleLineTypes, setInternalVisibleLineTypes] = useState<Set<string>>(
    new Set(LINE_TYPES)
  );
  const visibleLineTypes = externalVisibleLineTypes || internalVisibleLineTypes;

  // Determine if line drawing features are enabled
  const lineDrawingEnabled = !!onFeatureCreate;

  // Collect all polygon vertices for snap targets
  const polygonVertices = useMemo(() => {
    return roofSections.flatMap(
      (s) => s.polygon_coordinates || []
    );
  }, [roofSections]);

  // Collect existing line endpoints for snap targets
  const existingLineEndpoints = useMemo(() => {
    return roofFeatures.flatMap((f) => f.coordinates || []);
  }, [roofFeatures]);

  // Group-nudge for auto_ai overlays during review (registration offset fix)
  const applyOffset = (coords: Array<{ lat: number; lng: number }>, isAuto: boolean) =>
    isAuto && autoOffset && (autoOffset.lat !== 0 || autoOffset.lng !== 0)
      ? coords.map((c) => ({ lat: c.lat + autoOffset.lat, lng: c.lng + autoOffset.lng }))
      : coords;

  const calculatePerimeter = (coordinates: Array<{ lat: number; lng: number }>): number => {
    if (coordinates.length < 2) return 0;
    let perimeter = 0;
    for (let i = 0; i < coordinates.length; i++) {
      const current = new google.maps.LatLng(coordinates[i].lat, coordinates[i].lng);
      const next = new google.maps.LatLng(
        coordinates[(i + 1) % coordinates.length].lat,
        coordinates[(i + 1) % coordinates.length].lng
      );
      perimeter += google.maps.geometry.spherical.computeDistanceBetween(current, next);
    }
    return perimeter;
  };

  const getNextColor = (): string => {
    const colors = ['#9C27B0', '#00BCD4', '#4CAF50', '#FF9800', '#E91E63', '#00FFFF'];
    return colors[roofSections.length % colors.length];
  };

  // Handle line completion from the drawing hook
  const handleLineComplete = useCallback(
    (data: { type: LineType; coordinates: Array<{ lat: number; lng: number }>; lengthFt: number }) => {
      if (!onFeatureCreate || !measurementId) return;

      // Count existing features of this type for auto-naming
      const existingCount = roofFeatures.filter(
        (f) => f.feature_type === data.type
      ).length;

      const feature: Omit<RoofFeature, 'id' | 'created_at' | 'updated_at'> = {
        measurement: measurementId,
        name: `${LINE_TYPE_CONFIG[data.type].label} ${existingCount + 1}`,
        feature_type: data.type,
        length_ft: data.lengthFt,
        coordinates: data.coordinates,
        is_detected: false,
        confidence_score: 1.0,
      };

      onFeatureCreate(feature);
    },
    [onFeatureCreate, measurementId, roofFeatures]
  );

  // Line drawing hook
  const { cancelCurrentLine } = useLineDrawing({
    map: mapInstanceRef.current,
    activeLineType: (drawingState.mode === 'draw_line' ? drawingState.activeLineType : null) as LineType | null,
    isActive: drawingState.mode === 'draw_line' && !!drawingState.activeLineType,
    polygonVertices,
    existingLineEndpoints,
    onLineComplete: handleLineComplete,
    snapEnabled,
  });

  // Handle polygon completion from the drawing hook
  const handlePolygonComplete = useCallback(
    (data: { coordinates: Array<{ lat: number; lng: number }> }) => {
      const { coordinates } = data;

      const area = google.maps.geometry.spherical.computeArea(coordinates) * 10.764;
      const perimeter = calculatePerimeter(coordinates) * 3.281;

      const newSection: Omit<RoofSection, 'id' | 'created_at' | 'updated_at'> = {
        measurement: 0,
        name: `Roof Section ${roofSections.length + 1}`,
        polygon_coordinates: coordinates,
        area_sqft: Math.round(area * 100) / 100,
        perimeter_ft: Math.round(perimeter * 100) / 100,
        color: getNextColor(),
        is_active: true,
        source: 'manual',
        points: [],
        total_points: coordinates.length,
      };

      onSectionCreate?.(newSection);

      setDrawingState((prev) => ({
        ...prev,
        mode: 'view',
        isDrawing: false,
      }));
    },
    [onSectionCreate, roofSections.length]
  );

  const snapTargets = useMemo(
    () => [...polygonVertices, ...existingLineEndpoints],
    [polygonVertices, existingLineEndpoints]
  );

  // Polygon drawing hook (replaces the DrawingManager removed in Maps API v3.65)
  const { cancelCurrentPolygon } = usePolygonDrawing({
    map: mapInstanceRef.current,
    isActive: drawingState.mode === 'draw' && !readOnly,
    fillColor: '#9C27B0',
    snapTargets,
    onPolygonComplete: handlePolygonComplete,
    snapEnabled,
  });

  // Initialize map with drawing tools
  useEffect(() => {
    if (!mapContainer) return;

    let isMounted = true;

    const initMap = () => {
      if (!isMounted) return;

      if (!window.google || !window.google.maps) {
        setTimeout(initMap, 500);
        return;
      }

      try {
        const mapOptions: google.maps.MapOptions = {
          center: selectedLocation || center,
          zoom: selectedLocation ? 21 : 15,
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          tilt: 0,
          maxZoom: 22,
          disableDefaultUI: false,
          gestureHandling: 'greedy',
          disableDoubleClickZoom: true, // Prevent zoom on double-click (used for line finalize)
        };

        const map = new google.maps.Map(mapContainer, mapOptions);
        mapInstanceRef.current = map;

        loadExistingRoofSections();
        loadExistingFeatures();

        setLoading(false);
        setError(null);
      } catch (err) {
        setError(`Failed to initialize map: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setLoading(false);
      }
    };

    initMap();

    return () => {
      isMounted = false;
    };
  }, [mapContainer]);

  // Update map center when selectedLocation changes
  useEffect(() => {
    if (mapInstanceRef.current && selectedLocation) {
      mapInstanceRef.current.setCenter(selectedLocation);
      mapInstanceRef.current.setZoom(21);
    }
  }, [selectedLocation, zoom]);

  // Load existing roof sections as polygons on the map
  const loadExistingRoofSections = useCallback(() => {
    if (!mapInstanceRef.current) return;

    polygonsRef.current.forEach((polygon) => polygon.setMap(null));
    polygonsRef.current.clear();

    roofSections.forEach((section) => {
      if (!section.polygon_coordinates || section.polygon_coordinates.length < 3) return;

      const isLineMode = drawingState.mode === 'draw_line' || drawingState.mode === 'draw';
      const isPendingReview = reviewPending && section.source === 'auto_ai';
      const polygon = new google.maps.Polygon({
        paths: applyOffset(section.polygon_coordinates, section.source === 'auto_ai'),
        fillColor: section.color,
        fillOpacity: isPendingReview ? 0.12 : 0.3,
        strokeWeight: 2,
        strokeColor: section.color,
        strokeOpacity: isPendingReview ? 0.7 : 1,
        clickable: !readOnly && !isLineMode,
        editable: !readOnly && drawingState.mode === 'edit',
        draggable: false,
      });

      polygon.setMap(mapInstanceRef.current!);
      polygonsRef.current.set(section.id, polygon);

      polygon.addListener('click', () => {
        handlePolygonSelect(section);
      });

      if (drawingState.mode === 'edit') {
        const updateHandler = () => {
          updateAreaCalculation(polygon);
          editedSectionsRef.current.set(section.id, polygon.getPath().getArray());
          setEditedSections((prev) => {
            const newMap = new Map(prev);
            newMap.set(section.id, polygon.getPath().getArray());
            return newMap;
          });
        };
        polygon.getPath().addListener('set_at', updateHandler);
        polygon.getPath().addListener('insert_at', updateHandler);
        polygon.getPath().addListener('remove_at', updateHandler);
      }
    });
  }, [roofSections, drawingState.mode, reviewPending, autoOffset]);

  // Length (ft) of a polyline path — matches useLineDrawing's calculateLengthFt
  const calculatePathLengthFt = (path: google.maps.LatLng[]): number => {
    let totalMeters = 0;
    for (let i = 0; i < path.length - 1; i++) {
      totalMeters += google.maps.geometry.spherical.computeDistanceBetween(path[i], path[i + 1]);
    }
    return totalMeters * 3.28084;
  };

  const lineLabelPosition = (coords: Array<{ lat: number; lng: number }>) => {
    const midIdx = Math.floor((coords.length - 1) / 2);
    return {
      lat: (coords[midIdx].lat + coords[midIdx + 1].lat) / 2,
      lng: (coords[midIdx].lng + coords[midIdx + 1].lng) / 2,
    };
  };

  // Dash pattern for pending-review lines (matches the drawing preview style)
  const pendingDashIcons = (weight: number) => [
    {
      icon: {
        path: 'M 0,-0.5 0,0.5',
        strokeOpacity: 0.85,
        strokeWeight: weight,
        scale: 3,
      },
      offset: '0',
      repeat: '10px',
    },
  ];

  // Weight changes on dashed lines are invisible unless the dash icons are
  // updated too — always set both together.
  const setLineWeight = (polyline: google.maps.Polyline, featureId: number, weight: number) => {
    polyline.setOptions({
      strokeWeight: weight,
      ...(pendingReviewIdsRef.current.has(featureId)
        ? { icons: pendingDashIcons(weight) }
        : {}),
    });
  };

  // Load existing features as polylines on the map
  const loadExistingFeatures = useCallback(() => {
    if (!mapInstanceRef.current) return;

    // Clear existing polylines and labels
    polylinesRef.current.forEach((polyline) => polyline.setMap(null));
    polylinesRef.current.clear();
    labelMarkersRef.current.forEach((m) => m.setMap(null));
    labelMarkersRef.current.clear();

    roofFeatures.forEach((feature) => {
      if (!feature.coordinates || feature.coordinates.length < 2) return;

      const config = LINE_TYPE_CONFIG[feature.feature_type as LineType];
      if (!config) return;

      const isVisible = visibleLineTypes.has(feature.feature_type);
      const isPendingReview = reviewPending && feature.source === 'auto_ai';
      const isEditable = !readOnly && drawingState.mode === 'edit';

      if (isPendingReview) {
        pendingReviewIdsRef.current.add(feature.id);
      } else {
        pendingReviewIdsRef.current.delete(feature.id);
      }

      // A rebuild mid-edit (e.g. a refetch) must not revert vertex drags the
      // user hasn't saved yet — prefer the in-progress edited path.
      const editedPath = isEditable ? editedFeaturesRef.current.get(feature.id) : undefined;

      const offsetCoords = applyOffset(feature.coordinates, feature.source === 'auto_ai');
      const polyline = new google.maps.Polyline({
        path: editedPath ?? offsetCoords,
        strokeColor: config.color,
        strokeWeight: LINE_STROKE_WEIGHT,
        // Pending-review lines render dashed: transparent base stroke + dash icons
        strokeOpacity: isPendingReview ? 0 : LINE_STROKE_OPACITY,
        ...(isPendingReview ? { icons: pendingDashIcons(LINE_STROKE_WEIGHT) } : {}),
        map: mapInstanceRef.current!,
        clickable: !readOnly && drawingState.mode !== 'draw_line',
        editable: isEditable,
        visible: isVisible,
      });

      polylinesRef.current.set(feature.id, polyline);

      // Add length label at midpoint of the line
      if (feature.coordinates.length >= 2) {
        const position = lineLabelPosition(offsetCoords);

        const labelMarker = new google.maps.Marker({
          position,
          map: mapInstanceRef.current!,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 0, // invisible icon
          },
          label: {
            text: `${config.label[0]}: ${Number(feature.length_ft).toFixed(1)}'`,
            color: '#fff',
            fontSize: '11px',
            fontWeight: 'bold',
            className: 'roof-line-label',
          },
          clickable: false,
          visible: isVisible,
          zIndex: 100,
        });
        labelMarkersRef.current.set(feature.id, labelMarker);
      }

      // Vertex editing: mirror the polygon edit pattern for polylines
      if (isEditable) {
        const path = polyline.getPath();
        const editHandler = () => {
          const newPath = path.getArray();
          editedFeaturesRef.current.set(feature.id, newPath);

          // Live-update the length label text + position
          const labelMarker = labelMarkersRef.current.get(feature.id);
          if (labelMarker && newPath.length >= 2) {
            const coords = newPath.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
            const lengthFt = calculatePathLengthFt(newPath);
            labelMarker.setLabel({
              text: `${config.label[0]}: ${lengthFt.toFixed(1)}'`,
              color: '#fff',
              fontSize: '11px',
              fontWeight: 'bold',
              className: 'roof-line-label',
            });
            labelMarker.setPosition(lineLabelPosition(coords));
          }
        };
        path.addListener('set_at', editHandler);
        path.addListener('insert_at', editHandler);
        path.addListener('remove_at', editHandler);
      }

      // Click to select for deletion
      if (!readOnly) {
        polyline.addListener('click', () => {
          setSelectedFeatureId((prev) => (prev === feature.id ? null : feature.id));
        });

        // Hover highlight (read the highlight through a ref so hover changes
        // don't force overlay rebuilds)
        polyline.addListener('mouseover', () => {
          setLineWeight(polyline, feature.id, LINE_STROKE_WEIGHT_HOVER);
        });
        polyline.addListener('mouseout', () => {
          setLineWeight(
            polyline,
            feature.id,
            highlightedFeatureIdRef.current === feature.id
              ? LINE_STROKE_WEIGHT_HOVER
              : LINE_STROKE_WEIGHT
          );
        });
      }
    });
  }, [roofFeatures, visibleLineTypes, readOnly, drawingState.mode, reviewPending, autoOffset]);

  const handlePolygonSelect = (section: RoofSection) => {
    setDrawingState((prev) => ({
      ...prev,
      activeSection: section,
    }));
    setSelectedFeatureId(null);
  };

  const updateAreaCalculation = (polygon: google.maps.Polygon) => {
    const path = polygon.getPath();
    const coordinates = path.getArray().map((latLng) => ({
      lat: latLng.lat(),
      lng: latLng.lng(),
    }));

    const area = google.maps.geometry.spherical.computeArea(path) * 10.764;
    const perimeter = calculatePerimeter(coordinates) * 3.281;

    setAreaCalculation({
      area_sqft: Math.round(area * 100) / 100,
      perimeter_ft: Math.round(perimeter * 100) / 100,
      coordinates,
    });
  };

  // Drawing mode handlers
  const handleDrawingModeChange = async (mode: DrawingMode) => {
    if (readOnly) return;

    // Save any edited sections when leaving edit mode
    if (drawingState.mode === 'edit' && mode !== 'edit' && editedSections.size > 0) {
      for (const [sectionId, path] of editedSections.entries()) {
        const coordinates = path.map((latLng) => ({
          lat: latLng.lat(),
          lng: latLng.lng(),
        }));

        const area =
          google.maps.geometry.spherical.computeArea(path) * 10.764;
        const perimeter = calculatePerimeter(coordinates) * 3.281;

        onSectionUpdate?.(sectionId, {
          polygon_coordinates: coordinates,
          area_sqft: Math.round(area * 100) / 100,
          perimeter_ft: Math.round(perimeter * 100) / 100,
          total_points: coordinates.length,
        });
      }
      setEditedSections(new Map());
      editedSectionsRef.current = new Map();
    }

    // Save any edited lines when leaving edit mode (same pattern as sections)
    if (drawingState.mode === 'edit' && mode !== 'edit' && editedFeaturesRef.current.size > 0) {
      for (const [featureId, path] of editedFeaturesRef.current.entries()) {
        const coordinates = path.map((latLng) => ({
          lat: latLng.lat(),
          lng: latLng.lng(),
        }));
        onFeatureUpdate?.(featureId, {
          coordinates,
          length_ft: Math.round(calculatePathLengthFt(path) * 100) / 100,
        });
      }
      editedFeaturesRef.current = new Map();
    }

    // Cancel any in-progress line when switching modes
    if (drawingState.mode === 'draw_line' && mode !== 'draw_line') {
      cancelCurrentLine();
    }

    // Cancel any in-progress polygon when switching modes
    if (drawingState.mode === 'draw' && mode !== 'draw') {
      cancelCurrentPolygon();
    }

    setDrawingState((prev) => ({
      ...prev,
      mode,
      activeLineType: mode === 'draw_line' ? prev.activeLineType || 'ridge' : null,
    }));

    // Toggle polygon clickability based on mode
    // In draw_line mode, polygons must be non-clickable so clicks pass through to the map
    const polygonClickable = mode !== 'draw_line' && mode !== 'draw';
    polygonsRef.current.forEach((polygon) => {
      polygon.setOptions({ clickable: polygonClickable });
    });
    // Also make polylines non-clickable during line drawing
    polylinesRef.current.forEach((polyline) => {
      polyline.setOptions({ clickable: mode !== 'draw_line' });
    });

    switch (mode) {
      case 'draw':
        setDrawingState((prev) => ({ ...prev, isDrawing: true }));
        break;
      case 'draw_line':
        setDrawingState((prev) => ({
          ...prev,
          isDrawing: true,
          activeLineType: prev.activeLineType || 'ridge',
        }));
        break;
      case 'edit':
        polygonsRef.current.forEach((polygon) => {
          polygon.setEditable(true);
        });
        polylinesRef.current.forEach((polyline) => {
          polyline.setEditable(true);
        });
        break;
      case 'view':
        polygonsRef.current.forEach((polygon) => {
          polygon.setEditable(false);
        });
        polylinesRef.current.forEach((polyline) => {
          polyline.setEditable(false);
        });
        setDrawingState((prev) => ({
          ...prev,
          isDrawing: false,
          activeSection: undefined,
        }));
        setSelectedFeatureId(null);
        break;
    }
  };

  const handleLineTypeChange = (type: LineType) => {
    setDrawingState((prev) => ({
      ...prev,
      activeLineType: type,
    }));
  };

  // Delete selected roof section or feature
  const handleDelete = () => {
    if (selectedFeatureId) {
      onFeatureDelete?.(selectedFeatureId);
      setSelectedFeatureId(null);
      return;
    }

    if (drawingState.activeSection) {
      // A deleted outline must not be resurrected by the deferred edit flush.
      const sectionId = drawingState.activeSection.id;
      editedSectionsRef.current.delete(sectionId);
      setEditedSections(previous => {
        const next = new Map(previous);
        next.delete(sectionId);
        return next;
      });
      const polygon = polygonsRef.current.get(drawingState.activeSection.id);
      if (polygon) {
        polygon.setMap(null);
        polygonsRef.current.delete(drawingState.activeSection.id);
      }
      onSectionDelete?.(drawingState.activeSection.id);
      setDrawingState((prev) => ({ ...prev, activeSection: undefined }));
    }
  };

  // Flush unsaved vertex edits if the map unmounts without a mode change
  // (e.g. the user leaves Advanced mode while still in Edit mode).
  useEffect(() => {
    return () => {
      if (typeof google === 'undefined' || !google.maps?.geometry) return;
      for (const [featureId, path] of editedFeaturesRef.current.entries()) {
        const coordinates = path.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
        onFeatureUpdateRef.current?.(featureId, {
          coordinates,
          length_ft: Math.round(calculatePathLengthFt(path) * 100) / 100,
        });
      }
      for (const [sectionId, path] of editedSectionsRef.current.entries()) {
        const coordinates = path.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
        const area = google.maps.geometry.spherical.computeArea(path) * 10.764;
        const perimeter = calculatePerimeter(coordinates) * 3.281;
        onSectionUpdateRef.current?.(sectionId, {
          polygon_coordinates: coordinates,
          area_sqft: Math.round(area * 100) / 100,
          perimeter_ft: Math.round(perimeter * 100) / 100,
          total_points: coordinates.length,
        });
      }
    };
  }, []);

  // Reload polygons when roof sections change
  useEffect(() => {
    if (mapInstanceRef.current) {
      loadExistingRoofSections();
    }
  }, [roofSections, loadExistingRoofSections]);

  // Reload features when roofFeatures change
  useEffect(() => {
    if (mapInstanceRef.current) {
      loadExistingFeatures();
    }
  }, [roofFeatures, loadExistingFeatures]);

  // Update polyline visibility when visibleLineTypes changes
  useEffect(() => {
    polylinesRef.current.forEach((polyline, featureId) => {
      const feature = roofFeatures.find((f) => f.id === featureId);
      if (feature) {
        const isVisible = visibleLineTypes.has(feature.feature_type);
        polyline.setVisible(isVisible);
        const labelMarker = labelMarkersRef.current.get(featureId);
        if (labelMarker) {
          labelMarker.setVisible(isVisible && showDimensions);
        }
      }
    });
  }, [visibleLineTypes, roofFeatures, showDimensions]);

  // Toggle all dimension labels visibility
  useEffect(() => {
    labelMarkersRef.current.forEach((marker, featureId) => {
      const feature = roofFeatures.find((f) => f.id === featureId);
      const typeVisible = feature ? visibleLineTypes.has(feature.feature_type) : true;
      marker.setVisible(showDimensions && typeVisible);
    });
  }, [showDimensions]);

  // Handle external highlight
  useEffect(() => {
    polylinesRef.current.forEach((polyline, featureId) => {
      setLineWeight(
        polyline,
        featureId,
        highlightedFeatureId === featureId ? LINE_STROKE_WEIGHT_HOVER : LINE_STROKE_WEIGHT
      );
    });
  }, [highlightedFeatureId]);

  // Keyboard shortcut handler for line types
  useEffect(() => {
    if (drawingState.mode !== 'draw_line') return;

    const handler = (e: KeyboardEvent) => {
      // Skip if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const key = e.key.toLowerCase();
      const shortcutMap: Record<string, LineType> = {};
      for (const [type, config] of Object.entries(LINE_TYPE_CONFIG)) {
        shortcutMap[config.shortcut.toLowerCase()] = type as LineType;
      }

      if (shortcutMap[key]) {
        e.preventDefault();
        handleLineTypeChange(shortcutMap[key]);
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [drawingState.mode]);

  if (loading) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 'sm',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography level="body-sm">Loading drawing tools...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ height }}>
        <Alert color="danger">{error}</Alert>
      </Box>
    );
  }

  const selectedFeature = selectedFeatureId
    ? roofFeatures.find((f) => f.id === selectedFeatureId)
    : null;

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Styles for map label overlays */}
      <style>{`
        .roof-line-label {
          background: rgba(0, 0, 0, 0.7) !important;
          padding: 1px 5px !important;
          border-radius: 3px !important;
          border: none !important;
        }
      `}</style>
      {/* Toolbar */}
      <MapToolbar
        mode={drawingState.mode}
        activeLineType={(drawingState.activeLineType as LineType) || null}
        onModeChange={handleDrawingModeChange}
        onLineTypeChange={handleLineTypeChange}
        onDelete={handleDelete}
        hasSelection={!!drawingState.activeSection || !!selectedFeatureId}
        readOnly={readOnly}
        showLineDrawing={lineDrawingEnabled}
        snapEnabled={snapEnabled}
        onToggleSnap={() => setSnapEnabled((prev) => !prev)}
        showDimensions={showDimensions}
        onToggleDimensions={() => setShowDimensions((prev) => !prev)}
        onOpenGoogleEarth={
          selectedLocation
            ? () => {
                const { lat, lng } = selectedLocation;
                window.open(
                  `https://earth.google.com/web/@${lat},${lng},0a,500d,35y,0h,0t,0r`,
                  '_blank'
                );
              }
            : undefined
        }
      />

      {/* Mode Indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: 48,
          right: 10,
          zIndex: 1000,
        }}
      >
        <Chip
          variant="soft"
          size="sm"
          color={
            drawingState.mode === 'draw'
              ? 'primary'
              : drawingState.mode === 'draw_line'
                ? 'primary'
                : drawingState.mode === 'edit'
                  ? 'warning'
                  : 'neutral'
          }
        >
          {drawingState.mode === 'draw' && 'Drawing Section'}
          {drawingState.mode === 'draw_line' &&
            `Drawing ${
              drawingState.activeLineType
                ? LINE_TYPE_CONFIG[drawingState.activeLineType as LineType]?.label || ''
                : 'Line'
            }`}
          {drawingState.mode === 'edit' && 'Edit Mode'}
          {drawingState.mode === 'view' && 'View Mode'}
        </Chip>
      </Box>

      {/* Area Calculation Display */}
      {areaCalculation && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            zIndex: 1000,
            p: 2,
            backgroundColor: 'background.surface',
            borderRadius: 'sm',
            boxShadow: 'sm',
          }}
        >
          <Typography level="body-sm" sx={{ fontWeight: 'bold', mb: 1 }}>
            Live Calculation:
          </Typography>
          <Typography level="body-xs">
            Area: {areaCalculation.area_sqft.toLocaleString()} sq ft
          </Typography>
          <Typography level="body-xs">
            Perimeter: {areaCalculation.perimeter_ft.toLocaleString()} ft
          </Typography>
        </Box>
      )}

      {/* Selected Section Info */}
      {drawingState.activeSection && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            zIndex: 1000,
            p: 2,
            backgroundColor: 'background.surface',
            borderRadius: 'sm',
            boxShadow: 'sm',
          }}
        >
          <Typography level="body-sm" sx={{ fontWeight: 'bold', mb: 1 }}>
            Selected: {drawingState.activeSection.name}
          </Typography>
          <Typography level="body-xs">
            Area: {drawingState.activeSection.area_sqft?.toLocaleString() || 'N/A'} sq ft
          </Typography>
          <Typography level="body-xs">
            Points: {drawingState.activeSection.total_points}
          </Typography>
        </Box>
      )}

      {/* Selected Feature Info */}
      {selectedFeature && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            zIndex: 1000,
            p: 2,
            backgroundColor: 'background.surface',
            borderRadius: 'sm',
            boxShadow: 'sm',
          }}
        >
          <Typography level="body-sm" sx={{ fontWeight: 'bold', mb: 1 }}>
            Selected: {selectedFeature.name}
          </Typography>
          <Typography level="body-xs">
            Type: {LINE_TYPE_CONFIG[selectedFeature.feature_type as LineType]?.label || selectedFeature.feature_type}
          </Typography>
          <Typography level="body-xs">
            Length: {Number(selectedFeature.length_ft).toFixed(1)} ft
          </Typography>
        </Box>
      )}

      {/* Drawing instructions */}
      {drawingState.mode === 'draw' && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            p: 1,
            px: 2,
            backgroundColor: 'rgba(0,0,0,0.7)',
            borderRadius: 'sm',
            color: '#fff',
          }}
        >
          <Typography level="body-xs" sx={{ color: '#fff' }}>
            Click to place corners. Click the first corner, double-click, or right-click to close the section. Esc to cancel.
          </Typography>
        </Box>
      )}
      {drawingState.mode === 'draw_line' && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            p: 1,
            px: 2,
            backgroundColor: 'rgba(0,0,0,0.7)',
            borderRadius: 'sm',
            color: '#fff',
          }}
        >
          <Typography level="body-xs" sx={{ color: '#fff' }}>
            Click to place points. Double-click or right-click to finish line. Esc to cancel.
          </Typography>
        </Box>
      )}

      {/* Map Container */}
      <Box
        sx={{
          height,
          width: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 'sm',
          overflow: 'hidden',
        }}
      >
        <div
          ref={(el) => {
            mapRef.current = el;
            setMapContainer(el);
          }}
          style={{ height: '100%', width: '100%' }}
        />
      </Box>
    </Box>
  );
};
